import { ApiError, UnauthenticatedError } from '@lib/api';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
} from '@tanstack/react-query';

import { getClient, listClients, provisionOwner } from '../api/clients';
import { formatTarget, type ClientTarget } from '../schemas/target';

import type { ClientDetail, ProvisionedOwner, ClientSummary } from '../schemas/client';

/**
 * Server state for the clients screens.
 *
 * ## The target is part of every key
 *
 * `formatTarget` produces one string per deployment, and it goes in the key —
 * not in the fetcher alone. Two consequences, and both are the point: switching
 * environment refetches instead of showing the previous one's rows, and two
 * environments never share a cache entry. A key without it would render
 * production's clients under a `dev` heading after a switch, which is the
 * mistake this screen can least afford.
 */

export const clientKeys = {
    all: ['clients'] as const,
    list: (target: string) => [...clientKeys.all, 'list', target] as const,
    detail: (target: string, tenantId: string) =>
        [...clientKeys.all, 'detail', target, tenantId] as const,
};

/** Same policy as the flags screens: a 4xx is an answer, not a blip. */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useClients(target: ClientTarget) {
    const id = formatTarget(target);

    return useQuery<ClientSummary[]>({
        // `id` is null for an ephemeral target with no slot. Disabling rather
        // than guessing keeps "you have not chosen a slot" a different state
        // from "this slot has no clients" — they lead to different actions.
        queryKey: clientKeys.list(id ?? 'unaddressable'),
        queryFn: ({ signal }) => listClients(target, signal),
        enabled: id !== null,
        retry: retryUnlessClientError,
    });
}

export function useClient(target: ClientTarget, tenantId: string) {
    const id = formatTarget(target);

    return useQuery<ClientDetail | null>({
        queryKey: clientKeys.detail(id ?? 'unaddressable', tenantId),
        queryFn: ({ signal }) => getClient(target, tenantId, signal),
        enabled: id !== null && tenantId.length > 0,
        retry: retryUnlessClientError,
    });
}

/**
 * Finish a half-created client.
 *
 * Invalidates the listing on settle, not on success: a failed attempt can still
 * have created the person — the store write precedes the credential — so the
 * row's answer may have changed either way.
 */
export function useProvisionOwner(
    target: ClientTarget,
    tenantId: string,
): UseMutationResult<ProvisionedOwner, Error, { username: string; email: string }> {
    const queryClient = useQueryClient();
    const id = formatTarget(target);

    return useMutation({
        mutationFn: (owner: { username: string; email: string }) =>
            provisionOwner(target, tenantId, owner),
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: clientKeys.list(id ?? 'unaddressable'),
            });
        },
    });
}
